import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import {
  Button,
  Modal,
  CardBody,
  Row,
  Col,
  FormGroup,
  Input,
} from "reactstrap";
import CurrencyInput from "react-currency-input-field";
import { toast } from "react-toastify";
import api from "service/api";
import Loading from "components/Loading";

const ModalClientBudgetConfig = ({ clientId, clientName }) => {
  const [open, setOpen] = useState(false);
  const { handleSubmit, setValue } = useForm();
  const [loading, setLoading] = useState(false);
  const [budgetData, setBudgetData] = useState(null);
  const [budgetLimit, setBudgetLimit] = useState(0);
  const [alerts, setAlerts] = useState([]);
  const [newAlertPercentage, setNewAlertPercentage] = useState("");

  useEffect(() => {
    if (open && clientId) {
      fetchBudgetData();
    }
  }, [open, clientId]);

  const fetchBudgetData = async () => {
    setLoading(true);
    try {
      const response = await api.get(`client/budget/${clientId}`);
      if (response.data) {
        setBudgetData(response.data);
        setBudgetLimit(response.data.budget || 0);
        setAlerts(
          response.data.budgetAlarms
            ? response.data.budgetAlarms.map((percentage) => ({ percentage }))
            : [],
        );

        setValue("budgetLimit", response.data.budget);
      }
    } catch (error) {
      setBudgetData(null);
      setBudgetLimit(0);
      setAlerts([]);
    }
    setLoading(false);
  };

  const addAlert = () => {
    const percentage = parseInt(newAlertPercentage);

    if (!percentage || percentage < 1 || percentage > 99) {
      toast.error("A porcentagem deve ser um número entre 1 e 99!");
      return;
    }

    if (alerts.some((alert) => alert.percentage === percentage)) {
      toast.error("Já existe um alerta configurado para esta porcentagem!");
      return;
    }

    setAlerts([...alerts, { percentage }]);
    setNewAlertPercentage("");
  };

  const removeAlert = (index) => {
    setAlerts(alerts.filter((_, i) => i !== index));
  };

  const onSubmit = (data) => {
    setLoading(true);

    const budgetConfig = {
      budgetLimit: budgetLimit,
      alerts: alerts.map((alert) => ({ percentage: alert.percentage })),
    };

    api
      .patch(`/client/budget/${clientId}`, budgetConfig)
      .then(() => {
        toast.success("Configuração de orçamento salva com sucesso!");
        setOpen(false);
        setLoading(false);
      })
      .catch((error) => {
        toast.error("Erro ao salvar configuração de orçamento!");
        console.error(error);
        setLoading(false);
      });
  };

  return (
    <>
      <Button
        color="warning"
        type="button"
        size="sm"
        onClick={() => setOpen(!open)}
        title="Configurar orçamento"
        style={{
          background: "none",
          border: "none",
          color: "#f5a623",
          fontSize: "1.2em",
        }}
      >
        <i className="fas fa-dollar-sign" />
      </Button>

      <Modal
        className="modal-dialog-centered"
        isOpen={open}
        toggle={() => setOpen(!open)}
        size="md"
      >
        <div className="modal-header">
          <h5 className="modal-title">Configurar Orçamento - {clientName}</h5>
          <button
            aria-label="Close"
            className="close"
            data-dismiss="modal"
            type="button"
            onClick={() => setOpen(!open)}
          >
            <span aria-hidden={true}>×</span>
          </button>
        </div>

        <CardBody>
          {loading ? (
            <div className="d-flex justify-content-center p-4">
              <Loading />
            </div>
          ) : (
            <form onSubmit={handleSubmit(onSubmit)}>
              {budgetData === null && !loading && (
                <div className="alert alert-info mb-3">
                  <i className="fas fa-info-circle mr-2"></i>
                  Orçamento não encontrado. Configure um novo orçamento abaixo.
                </div>
              )}

              <div className="pl-lg-4">
                <Row>
                  <Col md="12">
                    <FormGroup>
                      <label
                        className="form-control-label"
                        htmlFor="budgetLimit"
                      >
                        Limite do Orçamento (USD)
                      </label>
                      <CurrencyInput
                        style={{
                          display: "block",
                          width: "100%",
                          height: "calc(1.5em + 1.25rem + 2px)",
                          padding: "0.625rem 0.75rem",
                          fontSize: "0.875rem",
                          fontWeight: "400",
                          lineHeight: "1.5",
                          color: "#8898aa",
                          backgroundColor: "#fff",
                          backgroundClip: "padding-box",
                          border: "1px solid #cad1d7",
                          borderRadius: "0.375rem",
                          boxShadow: "none",
                          transition:
                            "all 0.2s cubic-bezier(0.68, -0.55, 0.265, 1.55)",
                        }}
                        decimalsLimit={2}
                        prefix="$"
                        className="form-control-alternative"
                        id="budgetLimit"
                        decimalSeparator=","
                        groupSeparator="."
                        value={budgetLimit}
                        onValueChange={(value) => {
                          setBudgetLimit(
                            value ? Number(value.replace(",", ".")) : 0,
                          );
                        }}
                        required
                        autoComplete="off"
                      />
                    </FormGroup>
                  </Col>
                </Row>

                <Row>
                  <Col md="12">
                    <h6 className="heading-small text-muted mb-3">
                      Configurações de Alerta
                    </h6>
                    <FormGroup className="mb-3">
                      <label className="form-control-label">
                        Alerta quando consumo excede
                      </label>
                      <div className="d-flex align-items-center">
                        <div
                          className="input-group"
                          style={{ maxWidth: "200px" }}
                        >
                          <Input
                            type="number"
                            min="1"
                            max="100"
                            placeholder="Ex: 75"
                            value={newAlertPercentage}
                            onChange={(e) =>
                              setNewAlertPercentage(e.target.value)
                            }
                            onKeyPress={(e) => {
                              if (e.key === "Enter") {
                                e.preventDefault();
                                addAlert();
                              }
                            }}
                          />
                          <div className="input-group-append">
                            <span className="input-group-text">%</span>
                          </div>
                        </div>
                        <Button
                          type="button"
                          color="success"
                          size="sm"
                          className="ml-2"
                          onClick={addAlert}
                          disabled={!newAlertPercentage}
                        >
                          <i className="fas fa-plus" />
                        </Button>
                      </div>
                      <small className="text-muted">
                        Digite uma porcentagem entre 1 e 99%
                      </small>
                    </FormGroup>
                    {alerts.length > 0 && (
                      <FormGroup>
                        <label className="form-control-label">
                          Alertas Configurados
                        </label>
                        <div className="mt-2">
                          {alerts
                            .sort((a, b) => a.percentage - b.percentage)
                            .map((alert, index) => (
                              <div
                                key={index}
                                className="d-flex justify-content-between align-items-center p-2 mb-2"
                                style={{
                                  backgroundColor: "#f8f9fe",
                                  borderRadius: "0.375rem",
                                  border: "1px solid #e9ecef",
                                }}
                              >
                                <span className="text-dark">
                                  <i className="fas fa-bell mr-2" />
                                  Ao exceder {alert.percentage}% do orçamento
                                </span>
                                <Button
                                  type="button"
                                  color="danger"
                                  size="sm"
                                  outline
                                  onClick={() => removeAlert(index)}
                                  title="Remover alerta"
                                >
                                  <i className="fas fa-trash" />
                                </Button>
                              </div>
                            ))}
                        </div>
                      </FormGroup>
                    )}

                    {alerts.length === 0 && (
                      <div className="text-center text-muted py-3">
                        <i className="fas fa-bell-slash fa-2x mb-2" />
                        <p>Nenhum alerta configurado</p>
                        <small>
                          Adicione alertas para ser notificado quando o consumo
                          atingir determinadas porcentagens do orçamento.
                        </small>
                      </div>
                    )}
                  </Col>
                </Row>
              </div>

              <div className="modal-footer d-block">
                <Button type="submit" disabled={loading} color="primary">
                  {loading ? (
                    <Loading
                      divStyle={{ width: "105px" }}
                      loadingStyle={{ width: 30, height: 20 }}
                    />
                  ) : budgetData ? (
                    "Atualizar"
                  ) : (
                    "Salvar"
                  )}
                </Button>
                <Button
                  type="button"
                  color="secondary"
                  className="ml-2"
                  onClick={() => setOpen(!open)}
                >
                  Cancelar
                </Button>
              </div>
            </form>
          )}
        </CardBody>
      </Modal>
    </>
  );
};

export default ModalClientBudgetConfig;
